import {
	useQuery,
	useInfiniteQuery,
	InfiniteQueryObserverOptions,
	UseQueryOptions,
	UseBaseQueryOptions,
} from '@tanstack/react-query';

import { userApi } from 'src/api';
import {
	GetMeEventsParams,
	GetMeEvents,
	GetMeTicketsParams,
	GetMeTickets,
	GetPlacesResponse,
	GetBankAccountStatusResponse,
} from 'src/types/api';
import {
	User,
	BillingInfo,
	EventPromoter,
	BillingDataStatus,
} from 'src/types/dto';

import useUserStore from '../stores/useUserStore';
import { UserStatus, SignupSource } from 'src/legacy/Components/auth/_constants';
import { useUpdaterQuery } from './utils';

export const ME_EVENTS_PAGE_LIMIT = 12;

const getMe = () =>
	userApi
		.get<User>('me')
		.then(res => res.data)
		.catch(er => {
			const status = er?.status || er?.response?.status;
			if (status === 401) {
				const { clear } = useUserStore.getState();
				clear();
			}
			return er;
		});

const getMeBillingData = () =>
	userApi.get<BillingInfo>('me/billingdata').then(res => res.data);

const getMeBankAccountStatus = (userId: number) =>
	userApi
		.get<BillingDataStatus>(`me/billingdata/check?userId=${userId}`)
		.then(res => res.data);

const getMePromoterEvents = () =>
	userApi.get<EventPromoter[]>('promoter/me').then(res => res.data);

const getMePlaces = () =>
	userApi
		.get<GetPlacesResponse>('place', { params: { limit: 6 } })
		.then(res => res.data);

export const getMeEvents = async ({
	offset,
	limit = ME_EVENTS_PAGE_LIMIT,
}: GetMeEventsParams) => {
	return userApi
		.get<GetMeEvents>('event/my-events', {
			params: { limit, offset: offset ?? 0 },
		})
		.then(res => res.data);
};

const getMeTickets = ({ offset }: GetMeTicketsParams) =>
	userApi
		.get<GetMeTickets>('me/tickets', {
			params: { limit: ME_EVENTS_PAGE_LIMIT, offset },
		})
		.then(res => res.data);

export const useMeQuery = (options?: Partial<UseBaseQueryOptions>) => {
	const { email, accessToken, userStatus, signupSource } = useUserStore();

	const hasOtpParams = (() => {
		if (typeof window === 'undefined') return false;
		try {
			const sp = new URLSearchParams(window.location.search);
			return !!sp.get('otp') && !!sp.get('email');
		} catch {
			return false;
		}
	})();

	const isOtpPending = userStatus === UserStatus.OTP_PENDING;
	const isCheckoutRoute =
		typeof window !== 'undefined' &&
		window.location.pathname.includes('/checkout');

	const shouldDisableMeWhileOtpPending =
		isOtpPending &&
		(isCheckoutRoute ||
			(signupSource !== SignupSource.LANDING_PAGE &&
				signupSource !== SignupSource.CREATE_EVENT));

	const isPending = userStatus === UserStatus.PENDING;

	const isEnabled =
		accessToken !== '' &&
		userStatus !== UserStatus.PREACTIVE &&
		!shouldDisableMeWhileOtpPending &&
		!isPending &&
		(options?.enabled ?? true) &&
		!hasOtpParams;

	const query = useUpdaterQuery<User>({
		queryKey: ['me', email],
		queryFn: getMe,
		staleTime: Infinity,
		enabled: isEnabled,
	});

	return query;
};

export const useMeBillingDataQuery = (options = {}) => {
	const { email } = useUserStore();

	const query = useUpdaterQuery({
		queryKey: ['me/billingdata', email],
		queryFn: getMeBillingData,
		staleTime: Infinity,
		...options,
	});

	// if (!email) query.update(null);

	return query;
};

type UseMeEventsInfiniteQueryOptions = {
	enabled?: boolean;
	firstPageLimit?: number;
};

export const useMeEventsInfiniteQuery = ({
	enabled = true,
	firstPageLimit = ME_EVENTS_PAGE_LIMIT,
}: UseMeEventsInfiniteQueryOptions = {}) => {
	const { data } = useMeQuery();

	return useInfiniteQuery<GetMeEvents, Error>({
		queryKey: ['me/events', data?.email, firstPageLimit],
		initialPageParam: 0,
		queryFn: ({ pageParam = 0 }) =>
			getMeEvents({
				offset: pageParam as number,
				limit: pageParam === 0 ? firstPageLimit : ME_EVENTS_PAGE_LIMIT,
			}),
		getNextPageParam: (lastPage, allPages) => {
			const loadedItems = allPages.reduce(
				(acc, page) => acc + page.items.length,
				0
			);
			return loadedItems < lastPage.count ? loadedItems : undefined;
		},
		enabled: enabled && !!data?.email,
	});
};

export const useMeTicketsInfiniteQuery = (
	options: InfiniteQueryObserverOptions<GetMeTickets, Error>
) => {
	const { data } = useMeQuery();

	return useInfiniteQuery<GetMeTickets, Error>({
		queryKey: ['me/tickets', data?.email],
		initialPageParam: 0,
		queryFn: ({ pageParam = 0 }) =>
			getMeTickets({ offset: pageParam as number }),
		getNextPageParam: (lastPage, allPages) =>
			allPages.length < Math.ceil(lastPage.count / ME_EVENTS_PAGE_LIMIT)
				? allPages.length * ME_EVENTS_PAGE_LIMIT
				: undefined,
		// ...options,
	});
};

export const useMePromoterEventsQuery = (
	options: UseQueryOptions<EventPromoter[], Error>
) => {
	const { data } = useMeQuery();

	const { queryKey: incomingKeys = [], ...restOfOptions } = options;

	return useQuery<EventPromoter[], Error>({
		queryKey: ['me/promoter-events', data?.email, ...incomingKeys],
		queryFn: getMePromoterEvents,
		enabled: !!data?.promoter && (options.enabled ?? true),
		...restOfOptions,
	});
};

export const useMePlaces = (
	// options: UseQueryOptions<GetPlacesResponse, Error> = { queryKey: [] },
	options?: Partial<UseBaseQueryOptions>
) => {
	const { data } = useMeQuery();

	const query = useUpdaterQuery<GetPlacesResponse>({
		queryKey: ['me/places', data?.email],
		queryFn: getMePlaces,
		// ...options,
	});

	// if (!data) query.update(null);

	return query;
};

export const useMeAccountStatusQuery = (
	// options: UseQueryOptions<GetPlacesResponse, Error> = { queryKey: [] },
	options?: Partial<UseBaseQueryOptions>
) => {
	const { data: MeData } = useMeQuery();

	const query = useUpdaterQuery<GetBankAccountStatusResponse>({
		queryKey: ['getMeBankAccountStatus', MeData?.id],
		queryFn: () => getMeBankAccountStatus(MeData?.id),
		enabled: !!MeData?.id && options?.enabled,
		// ...options,
	});

	// if (!data) query.update(null);

	return query;
};